// ui/infoTooltip.js
// Reusable info icon component for explanatory tooltips

/**
 * Escape HTML special characters for safe tooltip content
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Render an info icon with tooltip
 * @param {string} text - Tooltip text (supports \n for line breaks)
 * @param {Object} options - Options
 * @param {string} options.size - 'sm', 'md', 'lg' (default: 'sm')
 * @param {string} options.color - Custom color (default: uses CSS variable)
 * @param {boolean} options.inline - If true, uses inline display (default: true)
 * @returns {string} HTML string
 */
export function renderInfoIcon(text, options = {}) {
    const { size = 'sm', color = '', inline = true } = options;

    const colorStyle = color ? `color: ${color};` : '';
    const displayStyle = inline ? '' : 'display: block;';

    // Convert newlines to <br> for tooltip display
    const tooltipText = escapeHtml(text).replace(/\n/g, '<br>');

    return `<span class="info-icon info-icon-${size}" style="${colorStyle}${displayStyle}" data-tooltip="${tooltipText}">&#9432;</span>`;
}

/**
 * Render info icon with a label
 * @param {string} label - Label text
 * @param {string} tooltip - Tooltip text
 * @param {Object} options - Options passed to renderInfoIcon
 * @returns {string} HTML string
 */
export function renderLabeledInfo(label, tooltip, options = {}) {
    return `<span class="labeled-info">${label} ${renderInfoIcon(tooltip, options)}</span>`;
}

/**
 * Render an estimate disclaimer info icon
 * Standard text for estimated values
 * @param {string} additionalText - Additional context
 * @returns {string} HTML string
 */
export function renderEstimateInfo(additionalText = '') {
    const baseText = 'This is an estimate and may vary.';
    const fullText = additionalText ? `${baseText}\n${additionalText}` : baseText;
    return renderInfoIcon(fullText, { size: 'sm' });
}

export default {
    renderInfoIcon,
    renderLabeledInfo,
    renderEstimateInfo
};
